import React from 'react';
import styled from 'styled-components/native';
import {Platform} from 'react-native';
import {useTheme} from 'styled-components';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import Config from 'react-native-config';
import {toggleSettingsModal} from '../../../connected-components/settingsModal/actions';
import {tr} from '../../../translations';
import Profile from '../../../screens/content/profile';
import {DrawerActions, useNavigation} from '@react-navigation/native';
import Icon from '../../icon';
import ProgressiveImage from '../../progressiveImage';
import TextGeneric from '../../textGeneric';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {useCustomHeaderHeight} from '../../../utils/hooks/useHeaderHeight';
import {ChatHeaderProps} from '../../../interfaces/components/chatHeader';
import useResponsiveDimensions from '../../../utils/hooks/useResponsiveDimensions';
import {Theme} from '../../../theme/lightTheme';
import {hdp} from '../../../utils/responsive';
import {navigationRef} from '../../../navigation';

const Container = styled.View<{
  headerHeight: number;
  topInset: number;
  width: number;
  compact: boolean;
  isLargeScreen: boolean;
}>`
  width: ${({width}) => width}px;
  height: ${({headerHeight}) => headerHeight}px;
  padding-horizontal: ${({width}) => width * 0.04}px;
  padding-top: ${({topInset}) =>
    Math.max(topInset, Platform.OS === 'web' ? 20 : hdp(5))}px;
  flex-direction: row;
  align-items: flex-start;
  justify-content: ${({compact}) => (compact ? 'flex-end' : 'space-between')};
  position: absolute;
  top: 0;
  ${({isLargeScreen, width}) =>
    isLargeScreen ? `right: ${width * 0.01}px;` : `left: ${width * 0.01}px;`}
  z-index: 99;
`;

const IconContainer = styled.TouchableOpacity<{size: number}>`
  width: ${({size}) => size * 1.3}px;
  height: ${({size}) => size * 1.3}px;
  background-color: ${({theme}) => theme.text.light};
  border-radius: 100px;
  align-items: center;
  justify-content: center;
  shadow-color: ${({theme}) => theme.text.dark};
  shadow-offset: 2px 4px;
  shadow-opacity: 0.1;
  shadow-radius: 4px;
  elevation: 1;
  margin-left: ${({theme}) => theme.text.s12}px;
`;

const ProfileContainer = styled.TouchableOpacity`
  flex-direction: row;
  align-items: center;
  flex: 1;
  padding-top: 1%;
`;

const BackIconContainer = styled.TouchableOpacity`
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const ProfileImageContainer = styled.View<{size: number}>`
  border-radius: 100px;
  width: ${({size}) => size * 1.3}px;
  aspect-ratio: 1;
  overflow: hidden;
  border-color: ${({theme}) => theme.homeBackground};
  border-width: 2px;
`;

const ProfileImage = styled(ProgressiveImage)`
  width: 100%;
  height: 100%;
`;

const ProfileContentContainer = styled.View`
  flex-direction: column;
  flex: 1;
`;

const Name = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s7}px;
  padding-horizontal: ${({theme}) => theme.text.s12}px;
  color: ${({theme}) => theme.text.dark};
`;

const Spetiality = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s8}px;
  padding-horizontal: ${({theme}) => theme.text.s12}px;
  color: ${({theme}) => theme.text.dark};
`;

const ChatHeader = ({
  style,
  customLeftIcon,
  onHistoryPress,
}: ChatHeaderProps): React.ReactElement => {
  const theme = useTheme() as Theme;
  const navigation = useNavigation();
  const headerHeight = useCustomHeaderHeight();
  const {top: topInset} = useSafeAreaInsets();
  const {currentUser, config} =
    useSelector((state: RootState) => state.auth) || {};
  const {wdp, isLargeScreen, isMobile} = useResponsiveDimensions();

  // On web & large screens, switch to compact mode
  const showCompact = Platform.OS === 'web' && isLargeScreen;

  const specialty = config?.specialties?.find(
    s => s.id === currentUser?.specialty_id,
  );
  const containerWidth = wdp(98) - (isLargeScreen ? Math.min(wdp(30), 350) : 0);
  const textSize = theme.text.s1;

  return (
    <Container
      headerHeight={headerHeight}
      topInset={topInset}
      width={containerWidth}
      compact={showCompact}
      isLargeScreen={isLargeScreen}
      style={style}>
      {!isMobile && customLeftIcon && customLeftIcon()}
      {!showCompact && (
        <>
          <BackIconContainer>
            <Icon
              name="back"
              color={theme.secondary}
              size={theme.text.s55}
              onPress={() => navigationRef?.current?.goBack()}
              style={{
                marginLeft: 0,
                marginRight: theme.text.s12,
              }}
            />
          </BackIconContainer>
          <ProfileContainer
            disabled={Config.DEMO_EMAIL === currentUser?.email}
            style={{opacity: Config.DEMO_EMAIL === currentUser?.email ? 0 : 1}}
            activeOpacity={0.8}
            onPress={() =>
              toggleSettingsModal({
                title: tr('profile.title'),
                component: () => <Profile />,
              })
            }>
            <ProfileImageContainer size={textSize}>
              <ProfileImage
                source={{uri: currentUser?.image_url}}
                defaultImage={require('../../../assets/images/user.png')}
              />
            </ProfileImageContainer>
            <ProfileContentContainer>
              <Name bold>
                {`${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`}
              </Name>
              {(currentUser?.email || '').trim().toLowerCase() !==
                Config.APPLE_EMAIL && (
                <Spetiality semiBold>{specialty?.name || ''}</Spetiality>
              )}
            </ProfileContentContainer>
          </ProfileContainer>

          <IconContainer
            activeOpacity={0.8}
            onPress={onHistoryPress}
            size={textSize}>
            <Icon name="history" color={theme.secondary} size={theme.text.s3} />
          </IconContainer>
        </>
      )}
      <IconContainer
        activeOpacity={0.8}
        onPress={() => navigation.dispatch(DrawerActions.toggleDrawer())}
        size={textSize}>
        <Icon name="menu" color={theme.secondary} size={theme.text.s5} />
      </IconContainer>
    </Container>
  );
};

export default ChatHeader;
